/**
 * Demo "as-of" clock.
 *
 * The API seed is a deterministic point-in-time snapshot anchored at
 * `SEED_ANCHOR_DATE` (2026-04-29; see
 * `apps/api/src/db/seeds/constants.ts`). The rent ledger, owner statements,
 * and historical close all stop on that date. If the demo viewer's browser
 * reads `new Date()` (April 2026, etc.), dashboard filters like "YTD" /
 * "QTD" / "30D" miss every seeded row and collapse to `$0`.
 *
 * `getAppNow()` is the single source of truth for "now" in the web app:
 *
 * - **Demo mode** (`VITE_DEMO_MODE === '1'` or host `demo.hummingbirdpmp.com`): returns `VITE_DEMO_REFERENCE_DATE`
 *   if set (expected to mirror `SEED_ANCHOR_DATE`), otherwise falls back to
 *   a hard-coded mirror so a misconfigured demo never leaks wall-clock time.
 * - **Production**: returns a fresh `new Date()` (wall clock).
 *
 * Only use this for viewer-facing metrics, filters, and "days-until"
 * countdowns. For created-at / updated-at audit timestamps keep using
 * `new Date()` — those belong to the real action, not the snapshot.
 */

/**
 * Hard-coded mirror of `SEED_ANCHOR_DATE`. Kept here (not imported from the
 * API package) so the web bundle doesn't pull in server-only seed modules.
 * Bump this if `SEED_ANCHOR_DATE` ever moves — both must agree.
 */
const DEMO_DEFAULT_REFERENCE_DATE_ISO = '2026-04-29T00:00:00.000Z';

function readEnv(name: string): string | undefined {
  // Prefer process.env so Vitest `vi.stubEnv` is visible (import.meta.env can lag stubs).
  try {
    const fromProcess = typeof process !== 'undefined' ? process.env?.[name] : undefined;
    if (fromProcess !== undefined) return fromProcess;
  } catch {
    // ignore — browser bundles may not expose process
  }
  try {
    const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
    return env?.[name];
  } catch {
    return undefined;
  }
}

function parseReferenceDate(raw: string | undefined): Date | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

/** Public demo SPA host (Vercel `hummingbird-property-management-platform-demo`). */
export const DEMO_PRODUCTION_HOSTNAME = 'demo.hummingbirdpmp.com';

export function isDemoMode(): boolean {
  if (readEnv('VITE_DEMO_MODE') === '1') return true;
  if (typeof globalThis !== 'undefined' && 'location' in globalThis) {
    const loc = (globalThis as unknown as Window).location;
    if (loc?.hostname === DEMO_PRODUCTION_HOSTNAME) return true;
  }
  return false;
}

/**
 * Returns the "as-of" date the app should treat as "now".
 *
 * In demo mode this is pinned to the seed snapshot date so YTD / QTD / 30D /
 * "overdue" all behave as if today were 2025-09-15. In production this is
 * the real wall clock.
 */
export function getAppNow(): Date {
  if (!isDemoMode()) {
    return new Date();
  }
  const override = parseReferenceDate(readEnv('VITE_DEMO_REFERENCE_DATE'));
  if (override) return override;
  return new Date(DEMO_DEFAULT_REFERENCE_DATE_ISO);
}

/** Convenience: same contract as `Date.now()` but honors demo mode. */
export function getAppNowMs(): number {
  return getAppNow().getTime();
}

/** ISO calendar date (`YYYY-MM-DD`) for statement/reconciliation pickers. */
export function getAppTodayIsoDate(): string {
  return getAppNow().toISOString().split('T')[0];
}

/** `YYYY-MM` period key aligned with demo snapshot. */
export function getAppYearMonth(): string {
  return getAppTodayIsoDate().slice(0, 7);
}
